// src/components/PeoplePlan/configs/workflowRegistry.js

const formatDateForSharePoint = (date) => {
  if (!date) return null;
  return new Date(date).toISOString();
};

export const WORKFLOW_REGISTRY = {
  6: {
    id: 6,
    name: "Exit",
    displayName: "Exit",
    description: "Track at-risk employees or those who have given notice",
    steps: [
      "Flag employees who are at risk of leaving or have given notice.",
      "Track succession planning status and timeline.",
      "Coordinate with HR for performance-related concerns.",
      "Use Harri for actual termination processing.",
    ],
    requiresForm: true,
    requiresSharePoint: false,
    icon: "exit",
    cssClass: "workflow-exit",
    formFields: [
      {
        name: "exitType",
        label: "Exit Risk Type",
        type: "select",
        required: true,
        options: ["Voluntary", "Involuntary"],
      },
      // Voluntary Exit Risk Fields
      {
        name: "riskLevel",
        label: "Risk Level",
        type: "select",
        required: true,
        options: [
          "High Risk (actively interviewing/given notice)",
          "Medium Risk (disengaged/unhappy)",
          "Low Risk (monitoring situation)",
        ],
        condition: { field: "exitType", equals: "Voluntary" },
      },
      {
        name: "expectedTimeline",
        label: "Expected Timeline",
        type: "select",
        required: true,
        options: [
          "Immediate (< 2 weeks)",
          "Short-term (2-4 weeks)",
          "Medium-term (1-3 months)",
          "Unknown",
        ],
        condition: { field: "exitType", equals: "Voluntary" },
      },
      {
        name: "successionStatus",
        label: "Succession Status",
        type: "select",
        required: true,
        options: [
          "Ready replacement identified",
          "Internal candidate developing",
          "Need to recruit externally",
          "No plan yet",
        ],
        condition: { field: "exitType", equals: "Voluntary" },
      },
      {
        name: "voluntaryNotes",
        label: "Exit Details (Optional)",
        type: "textarea",
        required: false,
        maxLength: 250,
        placeholder:
          "Provide specific details about the voluntary exit situation...",
        condition: { field: "exitType", equals: "Voluntary" },
      },
      // Involuntary Exit Risk Fields
      {
        name: "performanceIssueType",
        label: "Performance Issue Type",
        type: "select",
        required: true,
        options: [
          "Performance concerns",
          "Attendance issues",
          "Behavioral concerns",
          "Position elimination",
        ],
        condition: { field: "exitType", equals: "Involuntary" },
      },
      {
        name: "currentActionStage",
        label: "Current Action Stage",
        type: "select",
        required: true,
        options: [
          "Monitoring/Coaching",
          "Documented conversations",
          "PIP in progress",
          "Final warning issued",
        ],
        condition: { field: "exitType", equals: "Involuntary" },
      },
      {
        name: "hrAwareness",
        label: "HR is aware and involved",
        type: "checkbox",
        required: false,
        condition: { field: "exitType", equals: "Involuntary" },
      },
      {
        name: "involuntaryNotes",
        label: "Exit Details (Optional)",
        type: "textarea",
        required: false,
        maxLength: 250,
        placeholder:
          "Provide specific details about the performance/involuntary exit situation...",
        condition: { field: "exitType", equals: "Involuntary" },
      },
    ],
  },
  7: {
    id: 7,
    name: "Developing",
    displayName: "Developing",
    description: "Promote in next 90 days",
    steps: [
      "Identify high-potential employees using performance metrics and manager feedback.",
      "Review requirements and competencies for promotion with FLM.",
      "Assess current skills and identify gaps (e.g., leadership, decision-making).",
      "Provide leadership opportunities (delegate supervisory duties).",
      "Offer feedback & coaching through scheduled check-ins.",
    ],
    requiresForm: true,
    requiresSharePoint: false,
    icon: "developing",
    cssClass: "workflow-developing",
    formFields: [
      {
        name: "workflowNote",
        label: "Notes (Optional)",
        type: "textarea",
        required: false,
        maxLength: 500,
        placeholder:
          "Add notes about development plan, timeline, or specific areas of focus...",
      },
    ],
  },
  8: {
    id: 8,
    name: "Promote",
    displayName: "Promote",
    description: "Immediate Promotion",
    steps: ["Follow process in current HR promotion ticketing system."],
    requiresForm: true,
    requiresSharePoint: true,
    icon: "promote",
    cssClass: "workflow-promote",
    formFields: [
      {
        name: "newTitle",
        label: "New Job Title",
        type: "select",
        required: true,
        options: ["AM", "CM", "FLM", "MIT", "MUM", "RM", "SR MUM", "SL"],
      },
      {
        name: "oldRate",
        label: "Old Rate ($)",
        type: "number",
        required: true,
        step: "0.01",
      },
      {
        name: "newRate",
        label: "New Rate ($)",
        type: "number",
        required: true,
        step: "0.01",
      },
      {
        name: "effectiveDate",
        label: "Effective Date",
        type: "date",
        required: true,
        validation: "sunday",
      },
      {
        name: "promotionJustification",
        label: "Promotion Justification",
        type: "textarea",
        required: true,
        maxLength: 1000,
      },
    ],
    buildSharePointFields: async (
      formData,
      employee,
      roleConfig,
      currentUser,
      note
    ) => {
      const description =
        formData.promotionJustification +
        (note ? ` | Additional Notes: ${note}` : "") +
        ` --- ✨This ticket was created from the People Plan. Created by: ${currentUser.name} (${currentUser.username})✨`;

      return {
        Category: "Employment Change Request",
        Subcategory: "Promotion Request",
        Description: description,
        Employee_Name: employee.employee_name || "",
        Old_Title: roleConfig.oldTitle,
        New_Title: formData.newTitle,
        Old_Rate: parseFloat(formData.oldRate).toFixed(2),
        New_Rate: parseFloat(formData.newRate).toFixed(2),
        Store: employee.corp || "ERROR",
        market: employee.market || "",
        Effective_Date: formData.effectiveDate
          ? formatDateForSharePoint(formData.effectiveDate)
          : null,
        Additional_Notes: note || null,
      };
    },
  },
  9: {
    id: 9,
    name: "Improvement Plan",
    displayName: "Improvement Plan",
    description: "Performance Improvement Plan - PIP",
    steps: [
      "Work with your HRG to create, deliver, and monitor the PIP.",
      "Set clear performance expectations (e.g., speed of service, order accuracy).",
      "Provide regular feedback during weekly check-ins & performance reviews.",
      "Identify training and development opportunities.",
      "Schedule 30/60/90-day evaluations for ongoing development.",
    ],
    requiresForm: true,
    requiresSharePoint: false,
    icon: "improvement",
    cssClass: "workflow-improvement",
    formFields: [
      {
        name: "workflowNote",
        label: "Notes (Optional)",
        type: "textarea",
        required: false,
        maxLength: 500,
        placeholder:
          "Add notes about performance issues, improvement goals, or timeline...",
      },
    ],
  },
  10: {
    id: 10,
    name: "Transfer",
    displayName: "Transfer",
    description: "Internal Transfer",
    steps: ["Follow process for current Internal Transfer ticket."],
    requiresForm: true,
    requiresSharePoint: true,
    icon: "transfer",
    cssClass: "workflow-transfer",
    formFields: [
      {
        name: "transferWageChange",
        label: "Wage Change?",
        type: "select",
        required: true,
        options: ["N/A", "Increase", "Decrease"],
      },
      {
        name: "transferSelectedStore",
        label: "Transfer To Which Store",
        type: "store-select",
        required: true,
      },
      {
        name: "transferOldRate",
        label: "Old Rate ($)",
        type: "number",
        required: false,
        step: "0.01",
        condition: { field: "transferWageChange", notEquals: "N/A" },
      },
      {
        name: "transferNewRate",
        label: "New Rate ($)",
        type: "number",
        required: false,
        step: "0.01",
        condition: { field: "transferWageChange", notEquals: "N/A" },
      },
      {
        name: "transferEffectiveDate",
        label: "Effective Date",
        type: "date",
        required: true,
        validation: "sunday",
      },
      {
        name: "transferDetails",
        label: "Please provide the details of your request",
        type: "textarea",
        required: false,
        maxLength: 1000,
      },
    ],
    buildSharePointFields: async (
      formData,
      employee,
      roleConfig,
      currentUser,
      note
    ) => {
      const description =
        (formData.transferDetails || "") +
        (note ? ` | Additional Notes: ${note}` : "") +
        ` --- ✨This ticket was created from the People Plan. Created by: ${currentUser.name} (${currentUser.username})✨`;

      return {
        Category: "Employment Change Request",
        Subcategory: "Internal Transfer Request",
        Employee_Name: employee.employee_name || "",
        Description: description,
        Store: employee.corp || "ERROR",
        market: employee.market || "",
        Wage_Change: formData.transferWageChange,
        Old_Rate:
          formData.transferWageChange !== "N/A"
            ? parseFloat(formData.transferOldRate).toFixed(2)
            : null,
        New_Rate:
          formData.transferWageChange !== "N/A"
            ? parseFloat(formData.transferNewRate).toFixed(2)
            : null,
        Effective_Date: formData.transferEffectiveDate
          ? formatDateForSharePoint(formData.transferEffectiveDate)
          : null,
        Transfer_To_Which_Store: formData.transferSelectedStore,
        Additional_Notes: note || null,
      };
    },
  },
  11: {
    id: 11,
    name: "Request Position",
    displayName: "Request Position",
    description: "Request to fill a vacant position",
    steps: [
      "Identify the vacant position that needs to be filled.",
      "Specify the reason for vacancy and urgency level.",
      "Provide target start date for new hire.",
      "Submit request for HR review and approval.",
      "Track recruitment progress through The Bench.",
    ],
    requiresForm: true,
    requiresSharePoint: false,
    icon: "request",
    cssClass: "workflow-request-position",
    formFields: [
      {
        name: "vacancyReason",
        label: "Reason for Vacancy",
        type: "select",
        required: true,
        options: [
          "Resignation",
          "Termination",
          "Promotion",
          "Transfer",
          "New Position",
          "Leave of Absence",
        ],
      },
      {
        name: "urgencyLevel",
        label: "Urgency Level",
        type: "select",
        required: true,
        options: [
          "Critical (Immediate)",
          "High (Within 1 week)",
          "Medium (Within 2 weeks)",
          "Low (Within 1 month)",
        ],
      },
      {
        name: "targetStartDate",
        label: "Target Start Date",
        type: "date",
        required: true,
      },
      {
        name: "positionDetails",
        label: "Additional Details",
        type: "textarea",
        required: true,
        maxLength: 500,
        placeholder:
          "Provide any additional context about this position request...",
      },
    ],
  },
  // Update workflow 12 in WORKFLOW_REGISTRY
  12: {
    id: 12,
    name: "Position Training",
    displayName: "Position Training",
    description: "Track employee training for position certification",
    steps: [
      "Employee is automatically selected from the table.",
      "Training position is determined by the current role table.",
      "Set goal certification date.",
      "Document training plan and milestones.",
      "Track progress through regular check-ins.",
      "Complete certification upon successful training.",
    ],
    requiresForm: true,
    requiresSharePoint: false,
    requiresEmployeeSelection: true,
    routesToPositionTables: true, // Add this flag
    icon: "training",
    cssClass: "workflow-position-training",
    formFields: [
      {
        name: "trainingPosition",
        label: "Training for Position",
        type: "select",
        required: true,
        dynamicOptions: true,
        placeholder: "Select target position",
      },
      {
        name: "goalCertificationDate",
        label: "Goal Certification Date",
        type: "date",
        required: true,
        validation: "sunday",
      },
      {
        name: "trainingPlan",
        label: "Training Plan",
        type: "textarea",
        required: true,
        maxLength: 1000,
        placeholder:
          "Describe the training plan, milestones, and success criteria...",
      },
      {
        name: "currentSkillLevel",
        label: "Current Skill Level",
        type: "select",
        required: true,
        options: [
          "Beginner (0-25%)",
          "Developing (26-50%)",
          "Proficient (51-75%)",
          "Advanced (76-99%)",
        ],
      },
      {
        name: "workflowNote",
        label: "Additional Notes (Optional)",
        type: "textarea",
        required: false,
        maxLength: 500,
        placeholder: "Any additional notes about the training...",
      },
    ],
  },
  // PeoplePulse Workflows
  13: {
    id: 13,
    name: "Create Goals & OKRs",
    displayName: "Create Goals & OKRs",
    description: "Create a goal or OKR for the selected employee",
    steps: [
      "Define the goal title and description.",
      "Select goal type (Goal or OKR).",
      "Set start and due dates.",
      "Goal will be assigned to the selected employee.",
    ],
    requiresForm: true,
    requiresSharePoint: false,
    requiresEmployeeSelection: true,
    isPeoplePulseWorkflow: true,
    icon: "goal",
    cssClass: "workflow-goals",
    formFields: [
      {
        name: "goalTitle",
        label: "Goal Title",
        type: "text",
        required: true,
        maxLength: 200,
        placeholder: "Enter goal title...",
      },
      {
        name: "goalDescription",
        label: "Description",
        type: "textarea",
        required: true,
        maxLength: 1000,
        placeholder: "Describe the goal and expected outcomes...",
      },
      {
        name: "goalType",
        label: "Type",
        type: "select",
        required: true,
        options: ["Goal", "OKR"],
      },
      {
        name: "goalCategory",
        label: "Category",
        type: "select",
        required: false,
        options: ["Performance", "Development", "Team", "Personal", "Other"],
      },
      {
        name: "goalStartDate",
        label: "Start Date",
        type: "date",
        required: true,
      },
      {
        name: "goalDueDate",
        label: "Due Date",
        type: "date",
        required: true,
      },
    ],
  },
  14: {
    id: 14,
    name: "Create 1 on 1 Meeting",
    displayName: "Create 1 on 1 Meeting",
    description: "Schedule a 1-on-1 meeting with the selected employee",
    steps: [
      "Select the type of meeting.",
      "Choose the date, time, and duration.",
      "Add agenda or notes for the meeting.",
      "Click 'Schedule Meeting' to create the calendar event.",
    ],
    requiresForm: true,
    requiresSharePoint: false,
    requiresEmployeeSelection: true,
    isPeoplePulseWorkflow: true,
    isMeetingWorkflow: true,
    icon: "meeting",
    cssClass: "workflow-meeting",
    formFields: [
      {
        name: "meetingType",
        label: "Type of Meeting",
        type: "select",
        required: true,
        options: ["Weekly Check-in", "Bi-weekly Check-in", "Monthly Check-in"],
      },
      {
        name: "meetingDateTime",
        label: "Date & Time",
        type: "datetime-local",
        required: true,
      },
      {
        name: "meetingDuration",
        label: "Duration",
        type: "select",
        required: true,
        options: ["15 min", "30 min", "45 min", "60 min"],
      },
      {
        name: "meetingAgenda",
        label: "Agenda / Notes",
        type: "textarea",
        required: false,
        maxLength: 2000,
        placeholder: "Add agenda items or notes for this meeting...",
      },
    ],
  },
  15: {
    id: 15,
    name: "Create Performance Review",
    displayName: "Create Performance Review",
    description: "Create a performance review cycle for the selected employee",
    steps: [
      "Define the review cycle name.",
      "Select the review type and template.",
      "Set start and end dates for the review period.",
    ],
    requiresForm: true,
    requiresSharePoint: false,
    requiresEmployeeSelection: true,
    isPeoplePulseWorkflow: true,
    icon: "review",
    cssClass: "workflow-review",
    formFields: [
      {
        name: "cycleName",
        label: "Cycle Name",
        type: "text",
        required: true,
        maxLength: 200,
        placeholder: "e.g., Q4 2024 Performance Review",
      },
      {
        name: "reviewType",
        label: "Review Type",
        type: "select",
        required: true,
        options: ["Annual", "Quarterly", "Mid-Year", "Probationary", "Ad-hoc"],
      },
      {
        name: "reviewTemplate",
        label: "Template",
        type: "select",
        required: true,
        options: ["Standard Performance Review", "Management Review", "360 Feedback", "Self Assessment"],
      },
      {
        name: "reviewStartDate",
        label: "Start Date",
        type: "date",
        required: true,
      },
      {
        name: "reviewEndDate",
        label: "End Date",
        type: "date",
        required: true,
      },
    ],
  },
  16: {
    id: 16,
    name: "Create Feedback & Recognition",
    displayName: "Create Feedback & Recognition",
    description: "Give feedback or recognition to the selected employee",
    steps: [
      "Select whether to give Feedback or Recognition.",
      "Enter your message.",
      "Submit to record and notify the employee.",
    ],
    requiresForm: true,
    requiresSharePoint: false,
    requiresEmployeeSelection: true,
    isPeoplePulseWorkflow: true,
    icon: "feedback",
    cssClass: "workflow-feedback",
    formFields: [
      {
        name: "feedbackType",
        label: "Type",
        type: "select",
        required: true,
        options: ["Feedback", "Recognition"],
      },
      {
        name: "feedbackMessage",
        label: "Message",
        type: "textarea",
        required: true,
        maxLength: 2000,
        placeholder: "Enter your feedback or recognition message...",
        condition: { field: "feedbackType", equals: "Feedback" },
      },
      {
        name: "recognitionMessage",
        label: "Recognition Message",
        type: "textarea",
        required: true,
        maxLength: 2000,
        placeholder: "Enter your recognition message...",
        condition: { field: "feedbackType", equals: "Recognition" },
      },
    ],
  },
};

// Workflows to hide from dropdown
const HIDDEN_WORKFLOWS = ["Transfer", "Promote", "Exit", "Improvement Plan"];

// Get workflow options for dropdown
export const getWorkflowOptions = () => {
  return Object.values(WORKFLOW_REGISTRY)
    .filter((workflow) => !HIDDEN_WORKFLOWS.includes(workflow.displayName))
    .map((workflow) => workflow.displayName);
};

// Get workflow by name
export const getWorkflowByName = (name) => {
  return Object.values(WORKFLOW_REGISTRY).find((w) => w.displayName === name);
};

// Get workflow by ID
export const getWorkflowById = (id) => {
  return WORKFLOW_REGISTRY[id];
};
